import React, {useEffect, useState} from 'react'
import {CircularProgress} from '@mui/material'
import Swal from 'sweetalert2'
import PaginationComponent from '../PaginationComponent/Pagination'
import CreateTicket from './Components/CreateTicket'
import TicketingDetails from './Components/TicketingDetails'
import {getAssignees, getTicketList, getTicketStatus} from './TicketingAPI'
import styles from './TicketingStyles/TicketingStyles.module.scss'
import {assigneeDataType, ticketDataType, ticketStatusDataType} from './TicketingDataType'
import FilterTickets from './Components/FilterTickets'
import SearchProperty from '../property/SearchProperty'

function TicketingHome() {
  var user = localStorage.getItem('user_type')
  const [loading, setLoading] = useState<boolean>(false)
  const [noDataFound, setNoDataFound] = useState<boolean>(false)
  const [allTickets, setAllTickets] = useState<ticketDataType[]>([])
  const [assignee, setAssignee] = useState<assigneeDataType[]>()
  const [allStatus, setAllStatus] = useState<ticketStatusDataType[]>([])
  const [showCreateTicketModal, setShowCreateTicketModal] = useState<boolean>(false)
  const [showTicketingDetail, setShowTicketingDetail] = useState<string | null>(null)
  const [searchInputValue, setsearchInputValue] = useState<string>('');
  const [itemOffset, setItemOffset] = useState<number>(0)
  const [dataLength, setDataLength] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [selectedAssignee, setSelectedAssignee] = useState<assigneeDataType[]>()
  const [selectedStatus, setSelectedStatus] = useState<ticketStatusDataType[]>()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const itemsPerPage = 10
  const pageCount = Math.ceil(dataLength / itemsPerPage)

  // function to get all status for ticket
  const getTicketStatusAPI = async () => {
    if (user === 'Non-Tenant') {
      try {
        const {data: response} = await getTicketStatus()
        if (response.code === '200') {
          setAllStatus(response.data)
        } else if (response.code === '204') {
          setAllStatus(response.data)
        } else {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
            text: response.msg_details,
            allowOutsideClick: true,
          })
        }
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
        })
      }
    }
  }

  const formatDateYYYYMMDD = (date: Date) => {
    const formattedDate = date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    return formattedDate
  }

  // function to get list of tickets
  async function getListOfTickets(
    loader = true,
    Offset = itemOffset,
    assignee_uuid: string[] = selectedAssignee && selectedAssignee.length > 0
      ? selectedAssignee.map((item) => item.account_uuid)
      : [],
    ticket_status_uuid: string[] = selectedStatus && selectedStatus.length > 0
      ? selectedStatus.map((item) => item.ticket_status_uuid)
      : [],
    from_date: string = startDate ? `${formatDateYYYYMMDD(new Date(startDate))} 00:00:00` : '',
    to_date: string = endDate ? `${formatDateYYYYMMDD(new Date(endDate))} 23:59:59` : ''
  )
   {
    if (loader === true) {
      setLoading(true)
    }
    try {
      const {data: response} = await getTicketList(
        Offset,
        itemsPerPage,
        assignee_uuid,
        ticket_status_uuid,
        from_date,
        to_date,
        searchInputValue
      )
      if (response.code === '200') {
        setNoDataFound(false)
        setAllTickets(response.data.tickets)
        setDataLength(response.data.totalTickets)
      } else if (response.code === '204') {
        setNoDataFound(true)
        setAllTickets([])
        setDataLength(0)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
          text: response.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    } finally {
      if (loader === true) {
        setLoading(false)
      }
    }
  }

  // function to get list of assignees
  const getListAssignees = async () => {
    if (user === 'Non-Tenant') {
      try {
        const {data: response} = await getAssignees('')
        if (response.code === '200') {
          setAssignee(response.data)
        } else if (response.code === '204') {
          setAssignee(response.data)
        } else {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
            text: response.msg_details,
            allowOutsideClick: true,
          })
        }
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
        })
      }
    }
  }

  // fetching list of assignees onload
  useEffect(() => {
    getListAssignees()
    getTicketStatusAPI()
  }, [])
  
  // fetching list of tickets onchange of itemOffset
  useEffect(() => {
    getListOfTickets()
  }, [itemOffset, searchInputValue])

  // function to set the color for ticket status in ticketing table
  const setBadgeColor = (statusCode: string) => {
    switch (statusCode) {
      case '2678e901320d4f77bdb55bf93cf033f4':
        // for pending
        return `${styles.status_pending}`
      case '2af4c0b7805b4d7290b29e3cac039bt6':
        // for In Progress
        return 'badge-light-warning'
      case '25d3e90ce39d4f9f851ec753ca0114hs':
        // for Processing
        return `${styles.status_processing}`
      case '12c5cf0be05a4c929d25df38eef1646':
        // for In Review
        return `${styles.status_in_review}`
      case '27e6363840544d12a097dbfaf10260d':
        // for Blocked
        return 'badge-light-danger'
      case '48b2fde9986b4509a90cc8fd927e3en':
        // for Done
        return 'badge-light-success'
      case '1eb45c907ed64730bfe4b233870fa93b':
        // for Reopened
        return `${styles.status_reopened}`
      default:
        return 'badge-secondary'
    }
  }

  // function to format the date
  function formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }
    return new Intl.DateTimeFormat('en-US', options).format(date)
  }

  // function to convert the ticket number and remove zero from front of the number
  function convertTicketNumberFormat(inputStr: string) {
    let strippedStr = Number(inputStr.slice(2))
    return `${inputStr.slice(0, 2)}-${strippedStr}`
  }

  // function to calculate the hours diff from ticket created to present time
  // and changing the color and the title
  function diff_hours(dt1: Date, ticket_status_uuid: string) {
    if (ticket_status_uuid === '48b2fde9986b4509a90cc8fd927e3en') {
      return ['text-success', 'The ticket has been resolved.']
    }
    var diff = (new Date().getTime() - dt1.getTime()) / 1000
    diff /= 60 * 60
    let ans = Math.abs(Math.round(diff))
    if (ans >= 24) {
      return ['text-danger', 'The ticket was created over 24 hours ago.']
    } else if (ans >= 12) {
      return ['text-warning', 'The ticket was created over 12 hours ago.']
    } else {
      return ['text-muted', 'The ticket was created less than 12 hours ago.']
    }
  }
  return (
    <>
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='d-flex flex-column flex-column-fluid'>
          {/* Begin: header for ticketing page */}
          <div className='d-flex flex-wrap align-items-center gap-2 gap-lg-3 justify-content-between mb-5'>
            {/* Begin: Heading */}
            <h1
              className='page-heading d-flex text-dark fw-bold fs-3 my-0
            flex-column justify-content-center'
            >
              Ticket Board
            </h1>
            {/* End: Heading */}

            {/* Begin: Create ticket button */}
            {user === 'Non-Tenant' && (
              <div className="d-flex flex-wrap">
              <div className="me-4 mb-3">
              <SearchProperty
                setCurrentPage={setCurrentPage}
                setItemOffset={setItemOffset}
                setsearchInputValue={setsearchInputValue}
                placeholder="Ticket"
              />
              </div>
                <button
                  onClick={() => setShowCreateTicketModal(!showCreateTicketModal)}
                  className='btn btn-sm fw-bold btn-primary mb-3'
                >
                  Create New Ticket
                </button>
                {showCreateTicketModal && assignee && (
                  <CreateTicket
                    showCreateTicketModal={showCreateTicketModal}
                    handleClose={() => setShowCreateTicketModal(false)}
                    getListOfTickets={getListOfTickets}
                    setCurrentPage={setCurrentPage}
                    setItemOffset={setItemOffset}
                  />
                )}
              </div>
            )}
            {/* End: Create ticket button */}
          </div>
          <hr className="p-0 m-0 mb-4 border-dotted"/>
          {assignee && allStatus && assignee.length > 0 && allStatus.length > 0 && (
            <FilterTickets
              assignee={assignee}
              status={allStatus}
              getListOfTickets={getListOfTickets}
              selectedAssignee={selectedAssignee}
              setSelectedAssignee={setSelectedAssignee}
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              setCurrentPage={setCurrentPage}
              setItemOffset={setItemOffset}
              formatDateYYYYMMDD={formatDateYYYYMMDD}
              setShowTicketingDetail={setShowTicketingDetail}
            />
          )}

          {loading ? (
            // {* Begin: loader*}
            <div
              className='d-flex justify-content-center align-items-center vh-70'
              style={{height: '70vh'}}
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              {/* End: header for ticketing page */}
              {/* Begin: Table */}
              <div id='table div'>
                <div className='card mb-5 mb-xl-8'>
                  <div className='card-body py-3'>
                    <div className='table-responsive'>
                      {/* begin::Table */}
                      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        {/* begin::Table head */}
                        {!noDataFound && (
                          <thead>
                            <tr className='fw-bold text-muted'>
                              <th className='max-w-50px'>Ticket Number</th>
                              <th className='max-w-150px'>Title</th>
                              <th className='max-w-150px'>Property Name</th>
                              <th className='max-w-150px'>Assignee</th>
                              <th className='max-w-150px'>Created on</th>
                              <th className='max-w-100px'>Status</th>
                              <th className='max-w-150px'>Actions</th>
                            </tr>
                          </thead>
                        )}
                        {/* end::Table head */}
                        {/* begin::Table body */}
                        <tbody>
                          {noDataFound ? (
                            <tr>
                              <td colSpan={5}>
                                <span className='d-flex flex-column align-items-center flex-start p-10'>
                                  <img
                                    src='/media/svg/undraw/no_tickets.svg'
                                    alt='No document uploaded'
                                    className='h-200px w-200px'
                                  />
                                  <span className='fw-bold text-muted fs-1 text-center'>
                                    No tickets present
                                  </span>
                                </span>
                              </td>
                            </tr>
                          ) : (
                            allTickets?.map((ticket, index: number) => {
                              return (
                                <tr key={index}>
                                  <td className='max-w-50px'>
                                    <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                      {convertTicketNumberFormat(ticket.ticket_number)}
                                    </span>
                                  </td>
                                  <td className='max-w-150px'>
                                    <span
                                      className='text-dark fw-bold text-hover-primary d-block fs-6 text-truncate max-w-150px cursor-pointer'
                                      title={
                                        ticket.ticket_title.charAt(0).toUpperCase() +
                                        ticket.ticket_title.slice(1)
                                      }
                                      onClick={() => setShowTicketingDetail(ticket.ticket_uuid)}
                                    >
                                      {ticket.ticket_title.charAt(0).toUpperCase() +
                                        ticket.ticket_title.slice(1)}
                                    </span>
                                  </td>
                                  <td className='max-w-50px'>
                                    <span
                                      className='text-muted fw-semibold text-muted d-block fs-6 text-truncate max-w-100px'
                                      title={
                                        ticket?.propertyDtls && ticket.propertyDtls.length > 0
                                          ? ticket.propertyDtls[0].property_title
                                              .charAt(0)
                                              .toUpperCase() +
                                            ticket.propertyDtls[0].property_title.slice(1)
                                          : '-'
                                      }
                                    >
                                      {ticket?.propertyDtls && ticket.propertyDtls.length > 0
                                        ? ticket.propertyDtls[0].property_title
                                            .charAt(0)
                                            .toUpperCase() +
                                          ticket.propertyDtls[0].property_title.slice(1)
                                        : '-'}
                                    </span>
                                  </td>
                                  <td className='max-w-50px'>
                                    <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                      {ticket.assignee.length > 0
                                        ? ticket.assignee[0].assignee_account_name
                                            .charAt(0)
                                            .toUpperCase() +
                                          ticket.assignee[0].assignee_account_name.slice(1)
                                        : '-'}
                                    </span>
                                  </td>
                                  <td
                                    className='max-w-150px d-flex align-items-center'
                                    title={`${
                                      diff_hours(
                                        new Date(ticket.created_date_time),
                                        ticket.ticket_status_uuid
                                      )[1]
                                    }`}
                                  >
                                    <span>
                                      <i
                                        className={`bi bi-clock-fill fs-2 p-0 me-3
                                      ${
                                        diff_hours(
                                          new Date(ticket.created_date_time),
                                          ticket.ticket_status_uuid
                                        )[0]
                                      }
                                       `}
                                      ></i>
                                    </span>
                                    <span className='fw-semibold d-block fs-7 text-muted'>
                                      {formatDate(new Date(ticket.created_date_time))}
                                    </span>
                                  </td>
                                  <td className='max-w-100px'>
                                    <span
                                      className={`badge ${setBadgeColor(
                                        ticket.ticket_status_uuid
                                      )}`}
                                    >
                                      {ticket.ticket_status_name}
                                    </span>
                                  </td>
                                  <td className='max-w-150px'>
                                    <div className='d-flex flex-shrink-0'>
                                      <div
                                        className='border-0 btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                        onClick={() => setShowTicketingDetail(ticket.ticket_uuid)}
                                      >
                                        <i
                                          className='bi bi-eye-fill bi-4x fs-2'
                                          title='View Ticket'
                                        ></i>
                                      </div>

                                      {showTicketingDetail === ticket.ticket_uuid && (
                                        <TicketingDetails
                                          showTicketingDetail={showTicketingDetail}
                                          handleClose={() => setShowTicketingDetail(null)}
                                          ticket={ticket}
                                          convertTicketNumberFormat={convertTicketNumberFormat}
                                          setBadgeColor={setBadgeColor}
                                          formatDate={formatDate}
                                          getListOfTickets={getListOfTickets}
                                          allStatus={allStatus}
                                        />
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )
                            })
                          )}
                        </tbody>
                        {/* end::Table body */}
                      </table>
                      {/* end::Table */}
                    </div>
                  </div>
                </div>
              </div>
              {/* End: Table */}

              {/* Begin: Pagination */}
              {allTickets.length > 0 && pageCount > 1 && (
                <PaginationComponent
                  itemsPerPage={itemsPerPage}
                  dataLength={dataLength}
                  setItemOffset={setItemOffset}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  pageCount={pageCount}
                />
              )}
              {/* End: Pagination */}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default TicketingHome
